import { Reference, useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import CheckboxGroup from '@components/fields/CheckboxGroup';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import RichTextArea from '@components/fields/RichTextArea';
import Form from '@components/forms/Form';
import {
  ConsultReportDiscussionItemFragment as ConsultReportDiscussionItem,
  ConsultReportDiscussionItemCreateDocument,
  ConsultReportDiscussionItemCreateMutation,
  ConsultReportDiscussionItemCreateMutationVariables,
  ConsultReportDiscussionItemFragmentDoc,
  ConsultReportDiscussionItemUpdateDocument,
  ConsultReportDiscussionItemUpdateMutation,
  ConsultReportDiscussionItemUpdateMutationVariables,
  FocusAreaEnum
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';
import { optionsForFocusArea } from '@utils/optionUtils';

type FormData = {
  focusAreas: FocusAreaEnum[];
  nextSteps: string;
  notes: string;
  title: string;
};

type Props = {
  autofocus: boolean;
  consultReportDiscussionItem: ConsultReportDiscussionItem | undefined;
  consultReportId: string;
  journeyId: string;
  onCancel: () => void;
  onError: () => void;
  onSuccess: () => void;
};

const ConsultReportDiscussionItemForm = ({
  autofocus,
  consultReportDiscussionItem,
  consultReportId,
  journeyId,
  onCancel,
  onError,
  onSuccess
}: Props) => {
  const calculateDefaultValues = useCallback(
    () => ({
      focusAreas: consultReportDiscussionItem?.focusAreas ?? [],
      nextSteps: consultReportDiscussionItem?.nextSteps ?? '',
      notes: consultReportDiscussionItem?.notes ?? '',
      title: consultReportDiscussionItem?.title ?? ''
    }),
    [consultReportDiscussionItem]
  );

  const defaultValues: FormData = calculateDefaultValues();

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateConsultReportDiscussionItem] = useMutation<
    ConsultReportDiscussionItemUpdateMutation,
    ConsultReportDiscussionItemUpdateMutationVariables
  >(ConsultReportDiscussionItemUpdateDocument);

  const [createConsultReportDiscussionItem] = useMutation<
    ConsultReportDiscussionItemCreateMutation,
    ConsultReportDiscussionItemCreateMutationVariables
  >(ConsultReportDiscussionItemCreateDocument, {
    update: (store, { data }) => {
      if (data?.consultReportDiscussionItemCreate?.consultReportDiscussionItem) {
        store.modify({
          fields: {
            consultReportDiscussionItems(cachedRefs: readonly Reference[]) {
              const itemRef = store.writeFragment({
                data: data.consultReportDiscussionItemCreate?.consultReportDiscussionItem,
                fragment: ConsultReportDiscussionItemFragmentDoc,
                fragmentName: 'ConsultReportDiscussionItem'
              });

              if (itemRef) {
                return [...cachedRefs, itemRef];
              }
              return [...cachedRefs];
            }
          },
          id: store.identify({
            __typename: 'ConsultReport',
            id: data?.consultReportDiscussionItemCreate.consultReportDiscussionItem.consultReportId
          })
        });
      }
    }
  });

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      focusAreas: yup
        .array()
        .of(yup.string().oneOf(Object.values(FocusAreaEnum)).defined())
        .min(1, intl.formatMessage({ id: 'errors.consultReportDiscussionItem.focusAreas.blank' }))
        .required(intl.formatMessage({ id: 'errors.consultReportDiscussionItem.focusAreas.blank' })),
      nextSteps: yup
        .string()
        .required(intl.formatMessage({ id: 'errors.consultReportDiscussionItem.nextSteps.blank' })),
      notes: yup.string().required(intl.formatMessage({ id: 'errors.consultReportDiscussionItem.notes.blank' })),
      title: yup.string().required(intl.formatMessage({ id: 'errors.consultReportDiscussionItem.title.blank' }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    form.reset(calculateDefaultValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculateDefaultValues]);

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    if (consultReportDiscussionItem?.id) {
      return updateConsultReportDiscussionItem({
        variables: {
          ...form.getValues(),
          id: consultReportDiscussionItem.id
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.consultReportDiscussionItemUpdate,
          intl,
          onError,
          onSuccess: () => {
            form.reset();
            onSuccess();
          },
          setBaseErrors,
          setError: form.setError,
          txBase: 'errors.consultReportDiscussionItem'
        });
      });
    }
    return createConsultReportDiscussionItem({
      variables: {
        ...form.getValues(),
        consultReportId
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.consultReportDiscussionItemCreate,
        intl,
        onError,
        onSuccess: () => {
          form.reset();
          onSuccess();
        },
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.consultReportDiscussionItem'
      });
    });
  }, [
    consultReportId,
    consultReportDiscussionItem,
    createConsultReportDiscussionItem,
    form,
    intl,
    onError,
    onSuccess,
    updateConsultReportDiscussionItem
  ]);

  const focusAreaOptions = optionsForFocusArea(intl, false);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <Input<FormData>
        autofocus={autofocus}
        control={form.control}
        error={form.formState.errors.title}
        label={intl.formatMessage({ id: 'forms.consultReportDiscussionItem.titleLabel' })}
        name="title"
        required
      />
      <RichTextArea<FormData>
        control={form.control}
        error={form.formState.errors.notes}
        journeyId={journeyId}
        label={intl.formatMessage({ id: 'forms.consultReportDiscussionItem.notesLabel' })}
        name="notes"
        required
      />
      <RichTextArea<FormData>
        control={form.control}
        error={form.formState.errors.nextSteps}
        journeyId={journeyId}
        label={intl.formatMessage({ id: 'forms.consultReportDiscussionItem.nextStepsLabel' })}
        name="nextSteps"
        required
      />
      <CheckboxGroup<FormData>
        control={form.control}
        errors={form.formState.errors.focusAreas}
        label={intl.formatMessage({ id: 'forms.consultReportDiscussionItem.focusAreasLabel' })}
        name="focusAreas"
        options={focusAreaOptions}
        required
      />
      <FormButtons hookForm={form} onCancel={onCancel} />
    </Form>
  );
};

export default ConsultReportDiscussionItemForm;
